import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, Sparkles } from 'lucide-react';

type FloatingBriefPromptProps = {
  visible: boolean;
  query: string;
  onQueryChange: (value: string) => void;
  onSubmit: () => void;
  placeholder?: string;
};

export function FloatingBriefPrompt({
  visible,
  query,
  onQueryChange,
  onSubmit,
  placeholder = 'Raccontaci di cosa hai bisogno...',
}: FloatingBriefPromptProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [isDesktopExpanded, setIsDesktopExpanded] = useState(false);
  const desktopInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!visible) {
      setIsFocused(false);
      setIsDesktopExpanded(false);
    }
  }, [visible]);

  const focusDesktopInput = useCallback(() => {
    setIsDesktopExpanded(true);
    window.requestAnimationFrame(() => {
      desktopInputRef.current?.focus();
    });
  }, []);

  const handleDesktopBlur = useCallback(() => {
    setIsFocused(false);
    if (!query.trim()) {
      setIsDesktopExpanded(false);
    }
  }, [query]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  }, [onSubmit]);

  const promptShellStyle = {
    background: isFocused || isDesktopExpanded
      ? 'linear-gradient(180deg, #4e83f0 0%, #336cde 36%, #2558c6 100%)'
      : 'linear-gradient(180deg, #4174e0 0%, #2f66d6 38%, #214ea9 100%)',
  } as const;

  const sparkles = (
    <div className="relative flex h-9 w-9 shrink-0 items-center justify-center">
      <Sparkles
        size={17}
        className="text-[#2563EB]"
        style={{ animation: 'floatingBriefSparkle 2.8s ease-in-out infinite' }}
      />
      <Sparkles
        size={9}
        className="absolute right-[1px] top-[4px] text-[#60a5fa]"
        style={{ animation: 'floatingBriefSparkle 2.8s ease-in-out infinite 0.65s' }}
      />
      <Sparkles
        size={8}
        className="absolute bottom-[4px] left-[3px] text-[#93c5fd]"
        style={{ animation: 'floatingBriefSparkle 2.8s ease-in-out infinite 1.2s' }}
      />
    </div>
  );

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <>
      <style>{`
        @keyframes floatingBriefSparkle {
          0%, 100% { transform: translateY(0) scale(0.9); opacity: 0.6; }
          40% { transform: translateY(-1px) scale(1.08); opacity: 1; }
          65% { transform: translateY(-2px) scale(1.18); opacity: 0.9; }
        }
      `}</style>

      <form
        onSubmit={handleSubmit}
        className={`fixed inset-x-0 z-[2147483647] px-2 transition-all duration-500 ease-out md:hidden ${
          visible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-8 opacity-0'
        }`}
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.35rem)' }}
      >
        <div
          className="mx-auto max-w-[28rem] rounded-full p-[8px] shadow-[0_24px_42px_-24px_rgba(8,18,37,0.38)] transition-all duration-300"
          style={promptShellStyle}
        >
          <div className="flex items-center gap-3.5 rounded-full bg-[rgba(244,246,248,0.98)] pl-4 pr-2.5 py-2 backdrop-blur">
            {sparkles}

            <input
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSubmit();
                }
              }}
              enterKeyHint="go"
              placeholder={placeholder}
              aria-label={placeholder}
              className="min-w-0 flex-1 bg-transparent text-[15px] font-normal text-slate-500 outline-none placeholder:font-normal placeholder:text-slate-400"
            />

            <button
              type="submit"
              aria-label="Vai al brief"
              className={`flex h-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#2563EB] text-white shadow-[0_10px_22px_-12px_rgba(37,99,235,0.9)] transition-all duration-200 ${
                query.trim()
                  ? 'w-10 translate-x-0 scale-100 opacity-100'
                  : 'pointer-events-none w-0 translate-x-2 scale-95 opacity-0'
              }`}
            >
              <ArrowRight size={18} strokeWidth={2.4} />
            </button>
          </div>
        </div>
      </form>

      <form
        onSubmit={handleSubmit}
        className={`fixed bottom-6 right-6 z-[2147483647] hidden transition-all duration-500 ease-out md:block ${
          visible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-8 opacity-0'
        }`}
      >
        <div
          className={`overflow-hidden rounded-full p-[8px] shadow-[0_24px_42px_-24px_rgba(8,18,37,0.38)] transition-[width,transform,box-shadow] duration-300 ${
            isDesktopExpanded ? 'w-[40rem]' : 'w-[24rem] cursor-text'
          }`}
          style={promptShellStyle}
          onClick={() => {
            if (!isDesktopExpanded) {
              focusDesktopInput();
            }
          }}
        >
          <div className="flex items-center gap-3.5 rounded-full bg-[rgba(244,246,248,0.98)] pl-4 pr-2.5 py-2 backdrop-blur">
            {sparkles}

            <input
              ref={desktopInputRef}
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              onFocus={() => {
                setIsFocused(true);
                setIsDesktopExpanded(true);
              }}
              onBlur={handleDesktopBlur}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSubmit();
                }
                if (e.key === 'Escape' && !query.trim()) {
                  setIsDesktopExpanded(false);
                  setIsFocused(false);
                  desktopInputRef.current?.blur();
                }
              }}
              placeholder={placeholder}
              aria-label={placeholder}
              className={`min-w-0 flex-1 bg-transparent text-[15px] font-normal text-slate-500 outline-none placeholder:font-normal placeholder:text-slate-400 transition-opacity duration-200 ${
                isDesktopExpanded ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-85'
              }`}
            />

            <button
              type="submit"
              aria-label="Vai al brief"
              className={`flex h-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#2563EB] text-white shadow-[0_10px_22px_-12px_rgba(37,99,235,0.9)] transition-all duration-200 ${
                isDesktopExpanded || query.trim()
                  ? 'w-10 translate-x-0 scale-100 opacity-100'
                  : 'pointer-events-none w-0 translate-x-2 scale-95 opacity-0'
              }`}
            >
              <ArrowRight size={18} strokeWidth={2.4} />
            </button>
          </div>
        </div>
      </form>
    </>,
    document.body
  );
}
